import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ToastProvider } from './context/ToastContext'
import ProtectedRoute from './components/layout/ProtectedRoute'
import AppLayout from './components/layout/AppLayout'
import Spinner from './components/ui/Spinner'
import { ROUTES } from './lib/constants'
import PaywallGate from './components/layout/PaywallGate'

const Login                = lazy(() => import('./pages/public/Login'))
const Signup               = lazy(() => import('./pages/public/Signup'))
const ForgotPassword       = lazy(() => import('./pages/public/ForgotPassword'))
const ResetPassword        = lazy(() => import('./pages/public/ResetPassword'))
const LandingPage          = lazy(() => import('./pages/public/LandingPage'))
const Pricing              = lazy(() => import('./pages/public/Pricing'))
const Privacy              = lazy(() => import('./pages/public/Privacy'))
const Terms                = lazy(() => import('./pages/public/Terms'))
const Refund               = lazy(() => import('./pages/public/Refund'))
const Dashboard            = lazy(() => import('./pages/app/Dashboard'))
const Products             = lazy(() => import('./pages/app/Products'))
const ProductDetail        = lazy(() => import('./pages/app/ProductDetail'))
const Settings             = lazy(() => import('./pages/app/Settings'))
const Onboarding           = lazy(() => import('./pages/app/Onboarding'))
const OnboardingSync       = lazy(() => import('./pages/app/OnboardingSync'))
const Alerts               = lazy(() => import('./pages/app/Alerts'))
const Suppliers            = lazy(() => import('./pages/app/Suppliers'))
const EmailDrafts          = lazy(() => import('./pages/app/EmailDrafts'))
const Predictions          = lazy(() => import('./pages/app/Predictions'))
const Reports              = lazy(() => import('./pages/app/Reports'))
const ABCAnalysis          = lazy(() => import('./pages/app/ABCAnalysis'))
const PurchaseOrders       = lazy(() => import('./pages/app/PurchaseOrders'))
const PurchaseOrderDetail  = lazy(() => import('./pages/app/PurchaseOrderDetail'))
const Blog                 = lazy(() => import('./pages/public/Blog'))
const BlogPost             = lazy(() => import('./pages/public/BlogPost'))
const About                = lazy(() => import('./pages/public/About'))
const StockyAlternative    = lazy(() => import('./pages/public/StockyAlternative'))
const ShopifyPreventStockouts = lazy(() => import('./pages/public/ShopifyPreventStockouts'))
const BestShopifyInventoryApps = lazy(() => import('./pages/public/BestShopifyInventoryApps'))
const ShopifyInventorySoftware = lazy(() => import('./pages/public/ShopifyInventorySoftware'))
const SmallStoreInventoryApp = lazy(() => import('./pages/public/SmallStoreInventoryApp'))
const ShopifyABCAnalysis = lazy(() => import('./pages/public/ShopifyABCAnalysis'))
const ShopifyInventoryManagementGuide2026 = lazy(() => import('./pages/public/ShopifyInventoryManagementGuide2026'))
const StockyShutdown = lazy(() => import('./pages/public/StockyShutdown'))
const ShopifyDeadStock = lazy(() => import('./pages/public/ShopifyDeadStock'))
const ShopifyInventoryClothingStore = lazy(() => import('./pages/public/ShopifyInventoryClothingStore'))
const ShopifyInventoryAudit = lazy(() => import('./pages/public/ShopifyInventoryAudit'))
const ShopifyInventoryForecasting = lazy(() => import('./pages/public/ShopifyInventoryForecasting'))
const ShopifyInventoryFoodStore = lazy(() => import('./pages/public/ShopifyInventoryFoodStore'))
const ShopifyInventoryTracking = lazy(() => import('./pages/public/ShopifyInventoryTracking'))
const ShopifyPOSInventory = lazy(() => import('./pages/public/ShopifyPOSInventory'))
const ShopifyInventorySync = lazy(() => import('./pages/public/ShopifyInventorySync'))
const ShopifyWarehouseManagement = lazy(() => import('./pages/public/ShopifyWarehouseManagement'))
const ShopifyBoutiqueInventory = lazy(() => import('./pages/public/ShopifyBoutiqueInventory'))
const ShopifyInventoryDashboard = lazy(() => import('./pages/public/ShopifyInventoryDashboard'))
const ShopifyInventoryNotTracked = lazy(() => import('./pages/public/ShopifyInventoryNotTracked'))
const ShopifyBoutiqueInventoryStart = lazy(() => import('./pages/public/ShopifyBoutiqueInventoryStart'))
const ShopifyJewelryInventory = lazy(() => import('./pages/public/ShopifyJewelryInventory'))
const ShopifyInventoryPolicy = lazy(() => import('./pages/public/ShopifyInventoryPolicy'))
const ShopifyWholesaleInventory = lazy(() => import('./pages/public/ShopifyWholesaleInventory'))
const ClothingStoreInventoryManagement = lazy(() => import('./pages/public/ClothingStoreInventoryManagement'))
const ShopifyBestWMS = lazy(() => import('./pages/public/ShopifyBestWMS'))
const BoutiqueInventoryManagement = lazy(() => import('./pages/public/BoutiqueInventoryManagement'))
const JewelryInventoryManagement = lazy(() => import('./pages/public/JewelryInventoryManagement'))
const HowIBuiltDebnix = lazy(() => import('./pages/public/HowIBuiltDebnix'))
const Debnix170Days = lazy(() => import('./pages/public/Debnix170Days'))
const Admin = lazy(() => import('./pages/app/Admin'))

function PageLoader() {
  return (
    <div style={{
      height: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--color-bg)'
    }}>
      <Spinner size={32} />
    </div>
  )
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function RootRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (user) return <Navigate to={ROUTES.DASHBOARD} replace />
  return <LandingPage />
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--color-bg)', gap: '1rem'
      }}>
        <h2 style={{ color: 'var(--color-text)' }}>Something went wrong</h2>
        <p style={{ color: 'var(--color-text-muted)' }}>Our team has been notified. Please try refreshing.</p>
        <button onClick={resetError} style={{
          padding: '0.5rem 1.5rem', background: 'var(--color-primary)',
          color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer'
        }}>Try again</button>
      </div>
    )}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <Suspense fallback={<PageLoader />}>
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<RootRoute />} />

                <Route path={ROUTES.LOGIN}           element={<Login />} />
                <Route path={ROUTES.SIGNUP}          element={<Signup />} />
                <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route path={ROUTES.RESET_PASSWORD}  element={<ResetPassword />} />
                <Route path="/privacy"               element={<Privacy />} />
                <Route path="/terms"                 element={<Terms />} />
                <Route path="/pricing"               element={<Pricing />} />
                <Route path="/refund"                element={<Refund />} />
                <Route path="/blog"                  element={<Blog />} />
                <Route path="/blog/best-shopify-inventory-management-apps" element={<BestShopifyInventoryApps />} />
                <Route path="/blog/shopify-inventory-software" element={<ShopifyInventorySoftware />} />
                <Route path={ROUTES.STOCKY_ALTERNATIVE} element={<StockyAlternative />} />
                <Route path="/blog/shopify-inventory-management-2026-guide" element={<Navigate to="/blog/shopify-inventory-management-guide" replace />} />
                <Route path="/blog/shopify-inventory-app-small-store" element={<SmallStoreInventoryApp />} />
                <Route path="/blog/shopify-stocky-shutdown" element={<StockyShutdown />} />
                <Route path="/blog/shopify-dead-stock" element={<ShopifyDeadStock />} />
                <Route path="/blog/prevent-shopify-stockouts" element={<ShopifyPreventStockouts />} />
                <Route path="/blog/abc-analysis-shopify" element={<ShopifyABCAnalysis />} />
                <Route path="/blog/shopify-inventory-management-clothing-store" element={<ShopifyInventoryClothingStore />} />
                <Route path="/blog/shopify-inventory-audit" element={<ShopifyInventoryAudit />} />
                <Route path="/blog/shopify-inventory-tracking" element={<ShopifyInventoryTracking />} />
                <Route path="/blog/shopify-inventory-forecasting" element={<ShopifyInventoryForecasting />} />
                <Route path="/blog/shopify-inventory-management-food-store" element={<ShopifyInventoryFoodStore />} />
                <Route path="/blog/shopify-pos-inventory-management" element={<ShopifyPOSInventory />} />
                <Route path="/blog/shopify-inventory-out-of-sync" element={<ShopifyInventorySync />} />
                <Route path="/blog/shopify-warehouse-management" element={<ShopifyWarehouseManagement />} />
                <Route path="/blog/shopify-inventory-management-boutique" element={<ShopifyBoutiqueInventory />} />
                <Route path="/blog/shopify-inventory-dashboard" element={<ShopifyInventoryDashboard />} />
                <Route path="/blog/shopify-inventory-not-tracked" element={<ShopifyInventoryNotTracked />} />
                <Route path="/blog/how-much-inventory-to-start-a-boutique-shopify" element={<ShopifyBoutiqueInventoryStart />} />
                <Route path="/blog/shopify-inventory-management-jewelry-store" element={<ShopifyJewelryInventory />} />
                <Route path="/blog/shopify-inventory-policy" element={<ShopifyInventoryPolicy />} />
                <Route path="/blog/shopify-wholesale-inventory" element={<ShopifyWholesaleInventory />} />
                <Route path="/blog/best-wms-for-shopify" element={<ShopifyBestWMS />} />
                <Route path="/blog/boutique-inventory-management" element={<BoutiqueInventoryManagement />} />
                <Route path="/blog/jewelry-inventory-management" element={<JewelryInventoryManagement />} />
                <Route path="/blog/clothing-store-inventory-management" element={<ClothingStoreInventoryManagement />} />
                <Route path="/blog/how-i-built-debnix" element={<HowIBuiltDebnix />} />
                <Route path="/blog/170-days-of-debnix" element={<Debnix170Days />} />
                <Route path="/blog/shopify-stocky-alternative" element={<Navigate to="/stocky-alternative" replace />} />
                <Route path="/blog/:slug"            element={<BlogPost />} />
                <Route path="/about"                 element={<About />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute><Onboarding /></ProtectedRoute>
                } />
                <Route path="/onboarding/sync" element={
                  <ProtectedRoute><OnboardingSync /></ProtectedRoute>
                } />

                <Route element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }>
                  <Route path={ROUTES.DASHBOARD}        element={<PaywallGate><Dashboard /></PaywallGate>} />
                  <Route path={ROUTES.PRODUCTS}         element={<PaywallGate><Products /></PaywallGate>} />
                  <Route path="/products/:id"           element={<PaywallGate><ProductDetail /></PaywallGate>} />
                  <Route path={ROUTES.PREDICTIONS}      element={<PaywallGate><Predictions /></PaywallGate>} />
                  <Route path={ROUTES.ABC_ANALYSIS}     element={<PaywallGate><ABCAnalysis /></PaywallGate>} />
                  <Route path={ROUTES.PURCHASE_ORDERS}  element={<PaywallGate><PurchaseOrders /></PaywallGate>} />
                  <Route path="/purchase-orders/:id"    element={<PaywallGate><PurchaseOrderDetail /></PaywallGate>} />
                  <Route path={ROUTES.SUPPLIERS}        element={<PaywallGate><Suppliers /></PaywallGate>} />
                  <Route path={ROUTES.EMAIL_DRAFTS}     element={<PaywallGate><EmailDrafts /></PaywallGate>} />
                  <Route path={ROUTES.REPORTS}          element={<PaywallGate><Reports /></PaywallGate>} />
                  <Route path={ROUTES.ALERTS}           element={<Alerts />} />
                  <Route path={ROUTES.SETTINGS}         element={<Settings />} />
                  <Route path="/admin"                  element={<Admin />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  )
}